import { useEffect, useRef, useState } from 'react';

/**
 * useInView — fire an animation once when an element enters the viewport.
 *
 * Usage:
 *   const { ref, inView } = useInView();
 *   <div ref={ref} className={inView ? 'bb-anim-fade-up' : 'opacity-0'} />
 *
 * Options:
 *   threshold (default 0.15) — fraction of element visible before triggering
 *   rootMargin (default "0px 0px -10% 0px") — fire slightly before reaching edge
 *   once (default true) — disconnect observer after first trigger
 */
export const useInView = ({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  once = true,
} = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      // Fallback for older browsers / SSR: show immediately
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, inView };
};

export default useInView;
